import React, { useState, useEffect, useCallback, useMemo } from 'react';
// Tooltip in this file is recharts'. antd's is aliased so the two cannot be
// confused: a recharts Tooltip outside a chart renders nothing and fails silently.
import {
  Row, Col, Card, Statistic, Spin, Typography, Select, Space, Checkbox,
  Tooltip as InfoTip,
} from 'antd';
import {
  BankOutlined,
  LockOutlined,
  FundOutlined,
  SwapOutlined,
} from '@ant-design/icons';
import {
  BarChart, Bar, LineChart, Line, Legend,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import PageLayout from '../components/layout/PageLayout';
import PeriodToolbar from '../components/common/PeriodToolbar';
import api from '../libs/api';

const { Text } = Typography;

const ENTITY_COLORS = [
  '#54A2CC', '#05A677', '#f5b759', '#FA5252', '#845EC2',
  '#FF6F91', '#0081CF', '#FFC75F', '#008E9B', '#C34A36',
];

const fmt = (v) => {
  if (v == null) return '-';
  if (Math.abs(v) >= 1_000_000) return `$${(v / 1_000_000).toFixed(2)}M`;
  if (Math.abs(v) >= 1_000) return `$${(v / 1_000).toFixed(1)}K`;
  return `$${v.toFixed(2)}`;
};

export default function CashAccount() {
  const [period, setPeriod] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [prudential, setPrudential] = useState(null);
  const [selectedEntities, setSelectedEntities] = useState([]);

  const fetchData = useCallback(async (p) => {
    if (!p) return;
    setLoading(true);
    try {
      const res = await api.get('/api/cash-account/summary', { params: p });
      setData(res.data.data);
    } catch { /* handled by interceptor */ }
    setLoading(false);
  }, []);

  useEffect(() => { if (period) fetchData(period); }, [period, fetchData]);

  // Fetch prudential data (independent of period)
  useEffect(() => {
    api.get('/api/cash-account/prudential')
      .then(res => setPrudential(res.data.data))
      .catch(() => {});
  }, []);

  const isComparison = period?.comparison;
  const result = data?.results?.[0];
  const entities = (result?.entities || []).filter(e => e.balance > 0);
  // Its own memo so the fallback array does not become a new value every
  // render and re-run everything downstream that depends on it.
  const entityNames = useMemo(() => data?.entityNames || [], [data]);

  // For comparison mode: merge all snapshots from all periods into one timeline
  const allSnapshots = useMemo(() => {
    if (!isComparison) return [];
    return (data?.results || [])
      .flatMap(r => r.snapshots || [])
      .sort((a, b) => {
        const parseD = (s) => {
          if (!s) return 0;
          const p = s.split('/').map(Number);
          const y = p[2] < 100 ? p[2] + 2000 : p[2];
          return new Date(y, p[1] - 1, p[0]).getTime();
        };
        return parseD(a.date) - parseD(b.date);
      });
  }, [isComparison, data]);

  // Entity select options
  const entityOptions = useMemo(() =>
    entityNames.map(name => ({ label: name, value: name })),
    [entityNames]
  );

  const allSelected = selectedEntities.length === entityNames.length && entityNames.length > 0;
  const handleSelectAll = (e) => {
    setSelectedEntities(e.target.checked ? [...entityNames] : []);
  };

  // Determine which lines to show: if entities selected, show those; otherwise show Grand Total + Cash Available
  const showEntityLines = selectedEntities.length > 0;

  /**
   * What the entity filter can honestly change.
   *
   * The filter drove only the chart, so picking ShineHub left four group-wide
   * figures sitting above a single entity's line as though they described it.
   *
   * How far it can go is set by the sheet, and was checked against it: the ten
   * entity columns sum to the Grand Total column exactly, on every row. So a
   * selection's total and its movement are both exact. Restricted Cash is not
   * one of those columns - it is a separate total, unchanging week to week,
   * held inside one of the entity balances - so neither it nor Cash Available
   * can be attributed to an entity. Those two stay group-wide and say so,
   * rather than being silently filtered or quietly left to look filtered.
   */
  const isFiltered = selectedEntities.length > 0 && selectedEntities.length < entityNames.length;
  const selection = useMemo(() => {
    if (!isFiltered || !result) return null;
    const picked = new Set(selectedEntities);
    const sum = (list) => (list || [])
      .filter((e) => picked.has(e.name))
      .reduce((t, e) => t + (e.balance || 0), 0);
    const now = sum(result.entities);
    return { total: now, movement: now - sum(result.baselineEntities) };
  }, [isFiltered, result, selectedEntities]);

  const scopeNote = (attributable) => {
    if (!isFiltered) return null;
    return (
      <Text type="secondary" style={{ fontSize: 11, display: 'block' }}>
        {attributable
          ? `${selectedEntities.length} of ${entityNames.length} entities`
          : 'all entities — not split per entity'}
      </Text>
    );
  };

  return (
    <PageLayout>
      <div className="page-header">
        <h1>Cash Accounts</h1>
        <p>Weekly cash position across all entities (Google Sheets)</p>
      </div>
      <PeriodToolbar onChange={setPeriod} />
      <Spin spinning={loading}>
        {/* KPI Cards */}
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={6}>
            <Card style={{ minHeight: 130 }}>
              <Statistic
                title={(
                  <InfoTip title="The sheet reports the available and restricted split as a group total only, so this cannot be narrowed to the entities selected below.">
                    <span>Cash Available</span>
                  </InfoTip>
                )}
                value={result?.cashAvailable}
                formatter={(v) => fmt(v)}
                prefix={<BankOutlined style={{ color: '#05A677' }} />}
              />
              {result?.latestDate && <Text type="secondary" style={{ fontSize: 12 }}>As of {result.latestDate}</Text>}
              {scopeNote(false)}
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card style={{ minHeight: 130 }}>
              <Statistic
                title={(
                  <InfoTip title="A fixed amount held inside one of the entity balances and reported only as a group total, so it cannot be attributed to the entities selected below.">
                    <span>Restricted Cash</span>
                  </InfoTip>
                )}
                value={result?.restrictedCash}
                formatter={(v) => fmt(v)}
                prefix={<LockOutlined style={{ color: '#f5b759' }} />}
              />
              {result?.latestDate && <Text type="secondary" style={{ fontSize: 12 }}>As of {result.latestDate}</Text>}
              {scopeNote(false)}
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card style={{ minHeight: 130 }}>
              <Statistic
                title={isFiltered ? 'Selected Total' : 'Grand Total'}
                value={selection ? selection.total : result?.grandTotal}
                formatter={(v) => fmt(v)}
                prefix={<FundOutlined style={{ color: '#54A2CC' }} />}
              />
              {result?.latestDate && <Text type="secondary" style={{ fontSize: 12 }}>As of {result.latestDate}</Text>}
              {scopeNote(true)}
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card style={{ minHeight: 130 }}>
              <Statistic
                title="Period Movement"
                value={selection ? selection.movement : result?.periodMovement}
                formatter={(v) => fmt(v)}
                prefix={<SwapOutlined />}
                valueStyle={{
                  color: (selection ? selection.movement : result?.periodMovement) >= 0 ? '#05A677' : '#FA5252',
                }}
              />
              {result?.baselineDate && result?.latestDate && <Text type="secondary" style={{ fontSize: 12 }}>{result.baselineDate} → {result.latestDate}</Text>}
              {scopeNote(true)}
            </Card>
          </Col>
        </Row>

        {/* Comparison mode: Weekly Cash Position line chart with entity selector */}
        {isComparison && allSnapshots.length > 0 && (
          <Card
            style={{ marginTop: 16 }}
            title="Weekly Cash Position"
            extra={
              <Space size="small">
                <Checkbox checked={allSelected} onChange={handleSelectAll}>All</Checkbox>
                <Select
                  mode="multiple"
                  placeholder="Select entities..."
                  value={selectedEntities}
                  onChange={setSelectedEntities}
                  options={entityOptions}
                  style={{ minWidth: 280 }}
                  maxTagCount={3}
                  maxTagTextLength={12}
                  size="small"
                  allowClear
                />
              </Space>
            }
          >
            <ResponsiveContainer width="100%" height={360}>
              <LineChart
                data={allSnapshots}
                margin={{ top: 5, right: 20, bottom: 60, left: 20 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" angle={-30} textAnchor="end" fontSize={11} />
                <YAxis tickFormatter={(v) => fmt(v)} fontSize={11} />
                <Tooltip formatter={(v) => fmt(v)} />
                <Legend />
                {!showEntityLines && (
                  <>
                    <Line type="monotone" dataKey="grandTotal" stroke="#54A2CC" strokeWidth={2} dot={{ r: 3 }} name="Grand Total" />
                    <Line type="monotone" dataKey="cashAvailable" stroke="#05A677" strokeWidth={1.5} dot={{ r: 2 }} name="Cash Available" />
                  </>
                )}
                {showEntityLines && selectedEntities.map((name, i) => (
                  <Line
                    key={name}
                    type="monotone"
                    dataKey={name}
                    stroke={ENTITY_COLORS[entityNames.indexOf(name) % ENTITY_COLORS.length]}
                    strokeWidth={2}
                    dot={{ r: 2 }}
                    name={name}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </Card>
        )}

        {/* Single period: Entity Breakdown bar chart */}
        {!isComparison && entities.length > 0 && (
          <Card style={{ marginTop: 16 }} title="Cash by Entity">
            <ResponsiveContainer width="100%" height={320}>
              <BarChart
                data={entities}
                margin={{ top: 5, right: 20, bottom: 60, left: 20 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" angle={-30} textAnchor="end" fontSize={11} />
                <YAxis tickFormatter={(v) => fmt(v)} fontSize={11} />
                <Tooltip formatter={(v) => fmt(v)} />
                <Bar dataKey="balance" fill="#54A2CC" radius={[4, 4, 0, 0]} name="Balance" />
              </BarChart>
            </ResponsiveContainer>
          </Card>
        )}
      </Spin>

      {/* AEMO Prudential Position — side by side */}
      {prudential && (
        <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
          <Col xs={24} lg={12}>
            <Card title="VPP (SHINEHUB) — Prudential Outstandings" size="small">
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={prudential.vpp} margin={{ top: 5, right: 20, bottom: 40, left: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" angle={-30} textAnchor="end" fontSize={10} />
                  <YAxis tickFormatter={(v) => fmt(v)} fontSize={11} />
                  <Tooltip formatter={(v, name) => [fmt(v), name]} />
                  <Legend />
                  <Line type="monotone" dataKey="outstandings" stroke="#54A2CC" strokeWidth={2} dot={false} name="Outstandings" />
                  <Line type="monotone" dataKey="tradingLimit" stroke="#FA5252" strokeWidth={1.5} strokeDasharray="6 3" dot={false} name="Trading Limit" />
                </LineChart>
              </ResponsiveContainer>
            </Card>
          </Col>
          <Col xs={24} lg={12}>
            <Card title="Powow Power (POWOW) — Prudential Outstandings" size="small">
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={prudential.pp} margin={{ top: 5, right: 20, bottom: 40, left: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" angle={-30} textAnchor="end" fontSize={10} />
                  <YAxis tickFormatter={(v) => fmt(v)} fontSize={11} />
                  <Tooltip formatter={(v, name) => [fmt(v), name]} />
                  <Legend />
                  <Line type="monotone" dataKey="outstandings" stroke="#05A677" strokeWidth={2} dot={false} name="Outstandings" />
                  <Line type="monotone" dataKey="tradingLimit" stroke="#FA5252" strokeWidth={1.5} strokeDasharray="6 3" dot={false} name="Trading Limit" />
                </LineChart>
              </ResponsiveContainer>
            </Card>
          </Col>
        </Row>
      )}
    </PageLayout>
  );
}
